import { useState } from 'react'
import { Send, AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog'
import { useFulfillmentDryRun, useSubmitFulfillment } from '../hooks/useFulfillment'
import type { FulfillmentSubmitResult } from '../api/fulfillmentApi'

interface Props {
  shopifyOrderId: string | number | null
  merchantReference?: string | null
  disabled?: boolean
}

// Submit flow: open → dry-run preview (payload + submitEnabled) → confirm submit.
export function FulfillmentSubmitDialog({ shopifyOrderId, merchantReference, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState<FulfillmentSubmitResult | null>(null)
  const dryRun = useFulfillmentDryRun()
  const submit = useSubmitFulfillment()

  const openAndPreview = async () => {
    if (!shopifyOrderId) return
    setOpen(true)
    setPreview(null)
    const result = await dryRun.mutateAsync(shopifyOrderId)
    setPreview(result)
  }

  const doSubmit = async () => {
    if (!shopifyOrderId) return
    await submit.mutateAsync(shopifyOrderId)
    setOpen(false)
  }

  // In a dry-run, `reason` is either "dryRun" (submission enabled) or the
  // disabled message. So submission is enabled iff reason === 'dryRun'.
  const submitEnabled = preview?.reason === 'dryRun'
  const items = preview?.draft?.items || []

  return (
    <>
      <Button variant="default" size="sm" onClick={openAndPreview} disabled={disabled || !shopifyOrderId}>
        <Send className="mr-1 h-4 w-4" /> Submit
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Submit order {merchantReference ? `#${merchantReference}` : ''} to provider</DialogTitle>
            <DialogDescription>
              Review the print-ready payload built from this Shopify order, then submit to the provider.
            </DialogDescription>
          </DialogHeader>

          {dryRun.isPending && (
            <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Building payload…
            </div>
          )}

          {preview && (
            <div className="space-y-4">
              {!submitEnabled && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    Submission is <strong>disabled</strong> on the server
                    (<code>PRINT_FULFILLMENT_SUBMIT_ENABLED</code>). You can preview the payload, but the
                    confirm button is inactive until it's enabled.
                  </div>
                </div>
              )}

              <div className="rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="p-2">Internal SKU</th>
                      <th className="p-2">Provider SKU</th>
                      <th className="p-2">Paper</th>
                      <th className="p-2 text-center">Copies</th>
                      <th className="p-2">Border</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="p-2 font-mono text-xs">{it.internalSku}</td>
                        <td className="p-2 font-mono text-xs">{it.sku}</td>
                        <td className="p-2 text-xs">{it.paperType || '—'}{it.substrateWeight ? ` ${it.substrateWeight}` : ''}</td>
                        <td className="p-2 text-center">{it.copies}</td>
                        <td className="p-2 text-xs">{it.assetGenerated ? 'baked white border' : 'full bleed / as-is'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-muted-foreground">
                Shipping: {preview.draft?.shippingMethod} · Idempotency: <code>{preview.draft?.idempotencyKey}</code>
                {preview.draft?.callbackUrl ? ' · webhook callback set' : ' · no webhook callback configured'}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={doSubmit} disabled={!preview || !submitEnabled || submit.isPending}>
              {submit.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
              Confirm submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
